import React, {useEffect, useState} from "react";
import {Link, useLocation, useNavigate, useParams} from "react-router-dom";
import {ArrowLeft, Share2} from "lucide-react";
import PublicEditorialHeader from "../components/PublicEditorialHeader";
import {API_URL, IMAGE_HOST} from "../constants";

function unwrapArticle(response, slug) {
  const pending = [response];
  const seen = new Set();
  while (pending.length) {
    const value = pending.shift();
    if (!value || typeof value !== "object" || seen.has(value)) continue;
    seen.add(value);
    if (Array.isArray(value)) {
      const match = value.find((item) => item?.slug === slug);
      if (match) return match;
      pending.push(...value);
      continue;
    }
    if (value.slug === slug && (value.title || value.name)) return value;
    if ((value.title || value.name) && (value.content || value.body || value.excerpt)) return value;
    ["article", "item", "result", "news", "blog", "data", "items"].forEach((key) => {
      if (value[key]) pending.push(value[key]);
    });
  }
  return null;
}
function unwrapItems(response) {
  const root = response?.data ?? response;
  const body = root?.data ?? root;
  return Array.isArray(body) ? body : body?.items ?? body?.news ?? body?.blogs ?? body?.results ?? [];
}
function titleOf(article) { return article?.title ?? article?.name ?? "Untitled story"; }
function authorOf(article) {
  const author = article?.author;
  if (typeof author === "string") return author;
  return [author?.firstName ?? author?.first_name, author?.lastName ?? author?.last_name].filter(Boolean).join(" ") || author?.name || article?.authorName || article?.author_name || "Fillop";
}
function categoryOf(article) { return article?.category?.name ?? article?.categoryName ?? article?.category_name ?? (typeof article?.category === "string" ? article.category : ""); }
function dateOf(article) { return article?.publishedAt ?? article?.published_at ?? article?.published_date ?? article?.publishAt ?? article?.createdAt ?? article?.created_at; }
function dateLabel(value) { return value ? new Date(value).toLocaleDateString("en-US", {month: "long", day: "numeric", year: "numeric"}) : ""; }
function imageUrl(article) {
  const image = article?.coverImage ?? article?.cover_image ?? article?.imageUrl ?? article?.image_url ?? article?.image;
  if (!image) return "";
  return image.startsWith("http") || image.startsWith("//") ? image : `${IMAGE_HOST || ""}${image.startsWith("/") ? "" : "/"}${image}`;
}
function estimateReadTime(article) {
  const given = article?.readingTime ?? article?.readTime ?? article?.read_time;
  if (given) return given;
  const text = String(article?.content ?? "").replace(/<[^>]*>/g, " ").trim();
  return `${Math.max(1, Math.ceil(text.split(/\s+/).filter(Boolean).length / 220))} min read`;
}
const CATEGORY_STYLE = {
  WAEC: "bg-blue-50 text-blue-700 ring-1 ring-blue-200", JAMB: "bg-purple-50 text-purple-700 ring-1 ring-purple-200",
  NECO: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200", GCE: "bg-amber-50 text-amber-700 ring-1 ring-amber-200",
  "Study Tips": "bg-rose-50 text-rose-700 ring-1 ring-rose-200", News: "bg-slate-100 text-slate-700 ring-1 ring-slate-200",
};

function BlogFooter() {
  return <footer className="mt-20 border-t border-slate-100 bg-white"><div className="mx-auto flex min-h-14 max-w-7xl items-center justify-between px-6 text-xs text-slate-400"><Link to="/blog" className="text-lg font-extrabold text-blue-900 no-underline">Fillop <span className="text-orange-500">Blog</span></Link><span>© {new Date().getFullYear()} FillOp. All rights reserved.</span></div></footer>;
}

export default function BlogDetail() {
  const {slug} = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const linkedArticle = location.state?.article?.slug === slug ? location.state.article : null;
  const [article, setArticle] = useState(linkedArticle);
  const [morePosts, setMorePosts] = useState([]);
  const [loading, setLoading] = useState(!linkedArticle);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const backTo = location.state?.from || "/blog";

  useEffect(() => {
    const controller = new AbortController();
    const fallbackArticle = location.state?.article?.slug === slug ? location.state.article : null;
    if (fallbackArticle) setArticle(fallbackArticle);
    setLoading(!fallbackArticle);
    setError("");
    fetch(`${API_URL}/api/public/news/${encodeURIComponent(slug)}`, {signal: controller.signal})
      .then(async (response) => {
        if (response.ok) {
          const item = unwrapArticle(await response.json(), slug);
          if (item?.title || item?.name) return item;
        }
        const params = new URLSearchParams({search: slug, page: "1", limit: "20"});
        const fallbackResponse = await fetch(`${API_URL}/api/public/news?${params}`, {signal: controller.signal});
        if (!fallbackResponse.ok) throw new Error(response.status === 404 ? "Post not found." : "Could not load this post.");
        let matches = unwrapItems(await fallbackResponse.json());
        let item = matches.find((candidate) => candidate?.slug === slug);
        if (!item) {
          const allResponse = await fetch(`${API_URL}/api/public/news?page=1&limit=100&sort=newest`, {signal: controller.signal});
          if (allResponse.ok) {
            matches = unwrapItems(await allResponse.json());
            item = matches.find((candidate) => candidate?.slug === slug);
          }
        }
        if (!item?.title && !item?.name) throw new Error("Post not found.");
        return item;
      })
      .then((item) => {
        setArticle(item);
      })
      .catch((reason) => {
        if (reason.name === "AbortError") return;
        if (fallbackArticle) setArticle(fallbackArticle);
        else setError(reason.message || "Could not load this post.");
      })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    window.scrollTo(0, 0);
    return () => controller.abort();
  }, [slug]);

  useEffect(() => {
    fetch(`${API_URL}/api/public/news?page=1&limit=6&sort=newest`)
      .then((response) => response.ok ? response.json() : Promise.reject())
      .then((response) => setMorePosts(unwrapItems(response)))
      .catch(() => setMorePosts([]));
  }, []);

  useEffect(() => {
    if (article) document.title = article.metaTitle ?? article.seoTitle ?? `${titleOf(article)} | Fillop Blog`;
  }, [article]);

  const share = async () => {
    const shareData = {title: titleOf(article), text: article?.metaDescription ?? article?.excerpt ?? "", url: window.location.href};
    try {
      if (navigator.share) await navigator.share(shareData);
      else { await navigator.clipboard.writeText(window.location.href); setCopied(true); window.setTimeout(() => setCopied(false), 1800); }
    } catch { /* Share sheet dismissed. */ }
  };

  const author = article ? authorOf(article) : "";
  const category = article ? categoryOf(article) : "";
  const image = article ? imageUrl(article) : "";
  const bodyContent = article?.content ?? article?.body ?? article?.contentHtml ?? article?.content_html ?? article?.bodyHtml ?? article?.body_html ?? article?.excerpt ?? article?.summary ?? "";
  const bodyIsHtml = /<\/?[a-z][\s\S]*>/i.test(bodyContent);
  const tagValues = Array.isArray(article?.tags) ? article.tags : String(article?.keywords ?? "").split(",");
  const more = morePosts.filter((item) => (item.slug ?? item.id) !== slug).slice(0, 5);

  return <div className="min-h-screen bg-white text-slate-800">
    <PublicEditorialHeader showNavigation={false} />
    {loading ? <main className="flex min-h-[65vh] flex-col items-center justify-center gap-4"><div className="h-10 w-10 animate-spin rounded-full border-4 border-slate-100 border-t-blue-800" /><p className="text-sm font-medium text-slate-400">Loading post…</p></main>
      : error ? <main className="flex min-h-[65vh] flex-col items-center justify-center gap-3 px-6 text-center"><span className="text-5xl">⚠️</span><h1 className="font-serif text-lg font-semibold text-slate-700">Post not found</h1><p className="max-w-xs text-sm text-red-400">{error}</p><button onClick={() => navigate(backTo)} className="mt-2 rounded-xl bg-blue-800 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-900"><ArrowLeft className="mr-1 inline h-4 w-4" /> Back to blog</button></main>
        : article ? <>
          <div className="mx-auto max-w-6xl px-6 pt-7"><button onClick={() => navigate(backTo)} className="inline-flex items-center gap-1.5 border-0 bg-transparent py-2 text-sm font-extrabold text-slate-700 transition hover:text-blue-800"><ArrowLeft className="h-4 w-4" /> Back to Blog</button></div>
          <div className="mx-auto mt-4 max-w-6xl px-6">{image && <img src={image} alt={titleOf(article)} className="block h-[260px] w-full rounded-2xl object-cover object-top sm:h-[400px]" />}</div>
          <main className="mx-auto max-w-6xl px-6 py-10">
            <article className="mx-auto max-w-4xl">
              {category && <div className="mb-4"><span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-widest ${CATEGORY_STYLE[category] || "bg-slate-100 text-slate-700 ring-1 ring-slate-200"}`}><span className="h-1.5 w-1.5 rounded-full bg-blue-500" />{category}</span></div>}
              <h1 className="mb-4 text-slate-900" style={{fontFamily: "Georgia, serif", fontSize: "clamp(1.75rem, 4vw, 2.5rem)", lineHeight: 1.18, fontWeight: 700}}>{titleOf(article)}</h1>
              {article.metaDescription || article.excerpt ? <p className="mb-6 text-base leading-7 text-slate-500">{article.metaDescription ?? article.excerpt}</p> : null}
              <div className="mb-8 grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 border-b border-slate-100 pb-6">
                <div className="flex min-w-0 items-start gap-3 sm:items-center"><div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-900 text-sm font-bold text-white shadow-sm">{author.charAt(0).toUpperCase()}</div><div className="min-w-0"><p className="m-0 truncate text-sm font-semibold leading-tight text-slate-800">{author}</p><div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-400"><span>{dateLabel(dateOf(article))}</span><span>·</span><span>{estimateReadTime(article)}</span>{category && <><span>·</span><span className="font-medium text-blue-800">{category}</span></>}</div></div></div>
                <button type="button" onClick={share} aria-label={copied ? "Link copied" : "Share post"} title={copied ? "Link copied" : "Share post"} className="rounded-full border border-slate-200 p-2 text-slate-400 transition hover:border-blue-300 hover:text-blue-800"><Share2 className="h-4 w-4" /></button>
              </div>
              {bodyContent && (bodyIsHtml
                ? <div className="blog-article-content text-[1.05rem] leading-[1.9] text-slate-600" dangerouslySetInnerHTML={{__html: bodyContent}} />
                : <div className="blog-article-content text-[1.05rem] leading-[1.9] text-slate-600">{String(bodyContent).split(/\n\s*\n/).map((paragraph, index) => paragraph.trim() && <p key={index} className="whitespace-pre-line">{paragraph.trim()}</p>)}</div>)}
              {tagValues.length > 0 && <div className="mt-10 border-t border-slate-100 pt-6"><p className="mb-3 text-[11px] font-bold uppercase tracking-widest text-slate-400">Tags</p><div className="flex flex-wrap gap-2">{tagValues.map((tag) => (typeof tag === "string" ? tag : tag?.name ?? "").trim()).filter(Boolean).map((tag) => <span key={tag} className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">{tag}</span>)}</div></div>}
              {more.length > 0 && <div className="mt-14 border-t border-slate-100 pt-8"><p className="mb-4 text-sm font-bold italic uppercase tracking-widest text-red-400">More Posts</p><ul className="space-y-3">{more.map((post) => <li key={post.id ?? post.slug}><Link to={`/blog/${post.slug ?? post.id}`} state={{from: "/blog"}} className="group flex items-start gap-2 text-sm font-medium leading-snug text-blue-700 underline transition hover:text-blue-900"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-700 transition group-hover:scale-125" />{titleOf(post)}</Link></li>)}</ul></div>}
            </article>
          </main>
        </> : <main className="flex min-h-[65vh] flex-col items-center justify-center gap-3 px-6 text-center"><h1 className="font-serif text-lg font-semibold text-slate-700">Post details are unavailable</h1><p className="max-w-md text-sm text-slate-500">We couldn’t load this article. Please try again or return to the blog.</p><button onClick={() => navigate(backTo)} className="mt-2 rounded-xl bg-blue-800 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-900"><ArrowLeft className="mr-1 inline h-4 w-4" /> Back to blog</button></main>}
    <BlogFooter />
  </div>;
}
